import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Bell, Send, Users, User, CheckCircle2, Clock, 
  MessageSquare, Gift, Award, AlertCircle, History, Mail,
  ChevronDown, ChevronUp, X, Calendar, Trash2, UserCheck, UserX, Play,
  BarChart3, Eye, AlertTriangle, XCircle, CheckCheck, Loader2, Image, Upload, Link, Plus, Pencil
} from 'lucide-react';

const AdminNotifications = () => {
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('general');
  const [sendEmail, setSendEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Image upload state
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // GIF URL state
  const [gifUrl, setGifUrl] = useState('');
  
  // Link state
  const [links, setLinks] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  
  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' or 'later'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  // Audience targeting state
  const [audience, setAudience] = useState('all'); // 'all', 'paid', 'trial', 'never_tracked', 'specific'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [onlyReachable, setOnlyReachable] = useState(false); // Filter to users with email OR active push
  
  // Scheduled notifications state
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(true);
  const [processingScheduled, setProcessingScheduled] = useState(false);
  const [editingScheduled, setEditingScheduled] = useState(null); // For editing a scheduled notification
  const [savingScheduled, setSavingScheduled] = useState(false);
  
  // Delivery tracking state
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingBatchDetails, setLoadingBatchDetails] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  
  // Success state for better feedback
  const [sendSuccess, setSendSuccess] = useState(null); // { message: string, count: number }
  
  // Pre-send confirmation dialog state (safety net — shows target count BEFORE sending)
  const [confirmDialog, setConfirmDialog] = useState(null); // { count, sample, audience, isScheduled, scheduledFor }
  const [previewingAudience, setPreviewingAudience] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchHistory();
    fetchScheduledNotifications();
    fetchBatches();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await apiClient.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await apiClient.get('/admin/notifications/history');
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchScheduledNotifications = async () => {
    try {
      const res = await apiClient.get('/admin/notifications/scheduled');
      setScheduledNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch scheduled notifications:', err);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await apiClient.get('/admin/notifications/batches');
      setBatches(res.data);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchBatchDetails = async (batchId) => {
    setLoadingBatchDetails(true);
    try {
      const res = await apiClient.get(`/admin/notifications/batches/${batchId}`);
      setBatchDetails(res.data);
      setSelectedBatch(batchId);
      setDeliveryModalOpen(true);
    } catch (err) {
      toast.error('Failed to load delivery details');
      console.error('Failed to fetch batch details:', err);
    } finally {
      setLoadingBatchDetails(false);
    }
  };

  const resendBatch = (batch) => {
    // Prefill the compose form with everything we stored on the batch
    setTitle(batch.title || '');
    setMessage(batch.message || '');
    setNotificationType(batch.notification_type || 'general');
    setSendEmail(!!batch.send_email);
    setImageUrl(batch.image_url || '');
    setImagePreview(batch.image_url || null);
    setGifUrl(batch.gif_url || '');
    setLinks(batch.links || []);
    setAudience(batch.audience || 'all');
    // Specific-user lists and only_reachable aren't stored on batches; reset them.
    setSelectedUsers([]);
    setOnlyReachable(false);
    // Send immediately by default; admin can flip to "later" if desired.
    setScheduleMode('now');
    setScheduledDate('');
    setScheduledTime('');

    window.scrollTo({ top: 0, behavior: 'smooth' });

    if ((batch.audience || 'all') === 'specific') {
      toast.info('Compose form pre-filled. Re-select the specific users for this resend.', { duration: 6000 });
    } else {
      toast.success(`Ready to resend to "${batch.audience || 'all'}" audience — review and hit Send Now.`);
    }
  };

  const handleCleanupNotifications = async () => {
    if (!window.confirm('This will remove any broken notifications that cause the ghost red dot issue for all users. Continue?')) {
      return;
    }
    
    setCleaningUp(true);
    try {
      const res = await apiClient.post('/admin/notifications/cleanup');
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to cleanup notifications');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please enter a title and message');
      return;
    }

    if (audience === 'specific' && selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    if (scheduleMode === 'later' && (!scheduledDate || !scheduledTime)) {
      toast.error('Please select a date and time for scheduling');
      return;
    }

    // SAFETY NET: preview the audience so admin sees the exact recipient count
    // BEFORE we fire the send/schedule. This is the guardrail added after the
    // production incident where a scheduled notification was misconfigured and
    // went to every user.
    setPreviewingAudience(true);
    try {
      const previewRes = await apiClient.post('/admin/notifications/preview-audience', {
        audience,
        user_ids: audience === 'specific' ? selectedUsers : [],
        only_reachable: onlyReachable,
      });
      const { count, sample, reachability } = previewRes.data;
      const isScheduled = scheduleMode === 'later';
      const scheduledFor = isScheduled
        ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()
        : null;
      setConfirmDialog({ count, sample, audience, isScheduled, scheduledFor, reachability, onlyReachable });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview audience');
    } finally {
      setPreviewingAudience(false);
    }
  };

  const executeSend = async () => {
    setConfirmDialog(null);
    if (scheduleMode === 'later') {
      const sendAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      
      setSending(true);
      setSendSuccess(null);
      try {
        const res = await apiClient.post('/admin/notifications/schedule', {
          title: title.trim(),
          message: message.trim(),
          notification_type: notificationType,
          send_at: sendAt,
          audience,
          user_ids: audience === 'specific' ? selectedUsers : [],
          send_email: sendEmail,
          only_reachable: onlyReachable,
          image_url: imageUrl || null,
          gif_url: gifUrl.trim() || null,
          links: links.length > 0 ? links : null
        });
        
        setSendSuccess({
          message: res.data.message,
          title: title.trim(),
          isScheduled: true,
          scheduledFor: new Date(sendAt).toLocaleString()
        });
        
        toast.success(res.data.message);
        resetForm();
        fetchScheduledNotifications();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setSendSuccess(null), 10000);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to schedule notification');
      } finally {
        setSending(false);
      }
    } else {
      setSending(true);
      setSendSuccess(null);
      try {
        const payload = {
          title: title.trim(),
          message: message.trim(),
          notification_type: notificationType,
          user_ids: audience === 'specific' ? selectedUsers : [],
          send_email: sendEmail,
          audience: audience,
          only_reachable: onlyReachable,
          image_url: imageUrl || null,
          gif_url: gifUrl.trim() || null,
          links: links.length > 0 ? links : null
        };

        const res = await apiClient.post('/admin/notifications/send', payload);
        
        const sentCount = res.data.sent_count || res.data.message?.match(/\d+/)?.[0] || 0;
        setSendSuccess({
          message: res.data.message,
          title: title.trim(),
          sentCount: sentCount
        });
        
        toast.success(res.data.message);
        resetForm();
        fetchHistory();
        fetchBatches();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setSendSuccess(null), 10000);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to send notification');
      } finally {
        setSending(false);
      }
    }
  };

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setSelectedUsers([]);
    setAudience('all');
    setSendEmail(false);
    setOnlyReachable(false);
    setScheduleMode('now');
    setScheduledDate('');
    setScheduledTime('');
    setImageUrl('');
    setImagePreview(null);
    setGifUrl('');
    setLinks([]);
    setShowLinkForm(false);
    setNewLinkUrl('');
    setNewLinkLabel('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Link helper functions
  const addLink = () => {
    if (!newLinkUrl.trim() || !newLinkLabel.trim()) {
      toast.error('Please enter both URL and label');
      return;
    }
    let url = newLinkUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    setLinks([...links, { url, label: newLinkLabel.trim() }]);
    setNewLinkUrl('');
    setNewLinkLabel('');
    setShowLinkForm(false);
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, PNG, GIF, etc.)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImageUrl(res.data.url);
      setImagePreview(res.data.url);
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImageUrl('');
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelScheduled = async (notificationId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled notification?')) return;
    
    try {
      await apiClient.delete(`/admin/notifications/scheduled/${notificationId}`);
      toast.success('Scheduled notification cancelled');
      fetchScheduledNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel notification');
    }
  };

  const handleProcessScheduled = async () => {
    setProcessingScheduled(true);
    try {
      const res = await apiClient.post('/admin/notifications/process-scheduled');
      toast.success(res.data.message);
      fetchScheduledNotifications();
      fetchHistory();
    } catch (err) {
      toast.error('Failed to process scheduled notifications');
    } finally {
      setProcessingScheduled(false);
    }
  };

  // Edit a scheduled notification - load it into the form
  const handleEditScheduled = (scheduled) => {
    setEditingScheduled(scheduled);
    setTitle(scheduled.title || '');
    setMessage(scheduled.message || '');
    setNotificationType(scheduled.notification_type || 'announcement');
    setAudience(scheduled.audience || 'all');
    setSelectedUsers(scheduled.user_ids || []);
    setSendEmail(scheduled.send_email || false);
    setOnlyReachable(scheduled.only_reachable || false);
    setImageUrl(scheduled.image_url || '');
    setGifUrl(scheduled.gif_url || '');
    setLinks(scheduled.links || []);
    setScheduleMode('later');
    
    // Parse the scheduled date/time
    if (scheduled.send_at) {
      const sendDate = new Date(scheduled.send_at);
      setScheduledDate(sendDate.toISOString().split('T')[0]);
      setScheduledTime(sendDate.toTimeString().slice(0, 5));
    }
    
    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info('Editing scheduled notification - make your changes and click "Update Scheduled"');
  };

  // Save edits to a scheduled notification
  const handleSaveScheduledEdit = async () => {
    if (!editingScheduled) return;
    
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in title and message');
      return;
    }
    
    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select a date and time');
      return;
    }
    
    const sendAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    
    setSavingScheduled(true);
    try {
      await apiClient.put(`/admin/notifications/scheduled/${editingScheduled.id}`, {
        title: title.trim(),
        message: message.trim(),
        notification_type: notificationType,
        send_at: sendAt,
        audience,
        user_ids: audience === 'specific' ? selectedUsers : [],
        send_email: sendEmail,
        only_reachable: onlyReachable,
        image_url: imageUrl || null,
        gif_url: gifUrl.trim() || null,
        links: links.length > 0 ? links : null
      });
      toast.success('Scheduled notification updated!');
      resetForm();
      setEditingScheduled(null);
      fetchScheduledNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update notification');
    } finally {
      setSavingScheduled(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingScheduled(null);
    resetForm();
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const notificationTypes = [
    { value: 'general', label: 'General Message', icon: MessageSquare },
    { value: 'birthday', label: 'Birthday', icon: Gift },
    { value: 'milestone', label: 'Milestone', icon: Award },
    { value: 'reminder', label: 'Reminder', icon: Clock },
  ];

  const audienceOptions = [
    { value: 'all', label: 'All Users', icon: Users, desc: 'Paid + Trial users' },
    { value: 'paid', label: 'Paid Users Only', icon: UserCheck, desc: 'Excludes trial users' },
    { value: 'trial', label: 'Trial Users Only', icon: UserX, desc: 'Only free trial users' },
    { value: 'never_tracked', label: 'Never Tracked', icon: AlertCircle, desc: 'Users with zero logged actions' },
    { value: 'specific', label: 'Select Specific Users', icon: User, desc: 'Choose who receives it' },
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatScheduledDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const pendingScheduled = scheduledNotifications.filter(n => n.status === 'pending');

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <Bell className="text-[#EFB342]" />
              Send Notifications
            </h1>
            <p className="text-stone-600 mt-1">Send or schedule messages to users</p>
          </div>
          
          <Button
            variant="outline"
            onClick={handleCleanupNotifications}
            disabled={cleaningUp}
            className="border-orange-700 text-orange-400 hover:bg-orange-950/30 hover:border-orange-600"
            data-testid="cleanup-notifications-btn"
          >
            {cleaningUp ? (
              <>
                <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mr-2" />
                Cleaning...
              </>
            ) : (
              <>
                <AlertCircle size={16} className="mr-2" />
                Fix Ghost Red Dots
              </>
            )}
          </Button>
        </div>

        {/* Success Banner - Shows after sending */}
        {sendSuccess && (
          <div className={`mb-6 rounded-2xl p-4 animate-in slide-in-from-top duration-300 border ${
            sendSuccess.isScheduled 
              ? 'bg-purple-50 border-purple-300' 
              : 'bg-green-50 border-green-300'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  sendSuccess.isScheduled ? 'bg-purple-100' : 'bg-green-100'
                }`}>
                  {sendSuccess.isScheduled ? (
                    <Calendar size={22} className="text-purple-700" />
                  ) : (
                    <CheckCircle2 size={22} className="text-green-700" />
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold text-lg ${
                    sendSuccess.isScheduled ? 'text-purple-800' : 'text-green-800'
                  }`}>
                    {sendSuccess.isScheduled ? 'Notification Scheduled!' : 'Notification Sent Successfully!'}
                  </h3>
                  <p className="text-stone-800 text-sm">{sendSuccess.message}</p>
                  <p className="text-stone-600 text-xs mt-1">
                    Title: "{sendSuccess.title}"
                    {sendSuccess.isScheduled && ` • Scheduled for: ${sendSuccess.scheduledFor}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSendSuccess(null)}
                className="text-stone-500 hover:text-stone-900 p-2 rounded-lg hover:bg-stone-100 transition-colors"
                data-testid="dismiss-success-banner"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Send Form */}
          <div className="space-y-6">
            <div className="bg-white border border-stone-200 rounded-2xl p-6">
              {/* Editing Banner */}
              {editingScheduled && (
                <div className="mb-4 p-3 bg-purple-900/30 border border-purple-500/50 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pencil size={16} className="text-purple-400" />
                    <span className="text-purple-300 text-sm font-medium">
                      Editing scheduled notification
                    </span>
                  </div>
                  <button
                    onClick={handleCancelEdit}
                    className="text-stone-600 hover:text-stone-900 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
              
              <h2 className="font-heading text-xl font-bold text-stone-900 mb-6 flex items-center gap-2">
                <Send size={20} className="text-[#EFB342]" />
                {editingScheduled ? 'Edit Scheduled Notification' : 'Compose Notification'}
              </h2>

              <div className="space-y-5">
                {/* Send Now vs Schedule */}
                <div className="space-y-2">
                  <Label className="text-stone-700">When to Send</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setScheduleMode('now')}
                      className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                        scheduleMode === 'now'
                          ? 'bg-[#EFB342]/20 border-[#EFB342] text-[#EFB342]'
                          : 'bg-stone-50 border-stone-300 text-stone-600 hover:border-stone-300'
                      }`}
                      data-testid="send-now-btn"
                    >
                      <Send size={18} />
                      <span className="font-medium">Send Now</span>
                    </button>
                    <button
                      onClick={() => setScheduleMode('later')}
                      className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                        scheduleMode === 'later'
                          ? 'bg-purple-500/20 border-purple-500 text-purple-400'
                          : 'bg-stone-50 border-stone-300 text-stone-600 hover:border-stone-300'
                      }`}
                      data-testid="schedule-later-btn"
                    >
                      <Calendar size={18} />
                      <span className="font-medium">Schedule</span>
                    </button>
                  </div>
                </div>

                {/* Schedule Date/Time */}
                {scheduleMode === 'later' && (
                  <div className="grid grid-cols-2 gap-3 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                    <div>
                      <Label className="text-purple-300 text-sm">Date</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={getMinDate()}
                        className="bg-stone-50 border-stone-300 text-stone-900 mt-1"
                        data-testid="schedule-date"
                      />
                    </div>
                    <div>
                      <Label className="text-purple-300 text-sm">Time</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="bg-stone-50 border-stone-300 text-stone-900 mt-1"
                        data-testid="schedule-time"
                      />
                    </div>
                  </div>
                )}

                {/* Notification Type */}
                <div className="space-y-2">
                  <Label className="text-stone-700">Notification Type</Label>
                  <Select value={notificationType} onValueChange={setNotificationType}>
                    <SelectTrigger className="bg-stone-50 border-stone-300 text-stone-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-50 border-stone-200">
                      {notificationTypes.map((type) => (
                        <SelectItem 
                          key={type.value} 
                          value={type.value}
                          className="text-stone-700 focus:bg-[#EFB342]/20 focus:text-stone-900"
                        >
                          <div className="flex items-center gap-2">
                            <type.icon size={16} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label className="text-stone-700">Title</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., 🎉 Great News!"
                    className="bg-stone-50 border-stone-300 text-stone-900"
                    data-testid="notification-title"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label className="text-stone-700">Message</Label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write your notification message here..."
                    rows={4}
                    className="w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 text-stone-900 placeholder:text-stone-500 focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342] resize-none"
                    data-testid="notification-message"
                  />
                </div>

                {/* Image Upload */}
                <div className="space-y-2">
                  <Label className="text-stone-700 flex items-center gap-2">
                    <Image size={16} />
                    Attach Image (Optional)
                  </Label>
                  
                  {imagePreview ? (
                    <div className="relative rounded-xl overflow-hidden border border-stone-300 bg-stone-50">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full max-h-48 object-contain"
                      />
                      <button
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-stone-900 transition-colors"
                        title="Remove image"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#EFB342]/50 hover:bg-white transition-all"
                    >
                      {uploadingImage ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="animate-spin text-[#EFB342]" size={24} />
                          <span className="text-stone-600 text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="text-stone-500" size={24} />
                          <span className="text-stone-600 text-sm">Click to upload an image</span>
                          <span className="text-stone-400 text-xs">JPEG, PNG, GIF up to 5MB</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                </div>

                {/* GIF URL Input */}
                <div className="space-y-2">
                  <Label className="text-stone-700 flex items-center gap-2">
                    <Image size={16} />
                    GIF URL (Optional)
                  </Label>
                  <Input
                    value={gifUrl}
                    onChange={(e) => setGifUrl(e.target.value)}
                    placeholder="https://media.giphy.com/media/xxxx/giphy.gif"
                    className="bg-stone-50 border-stone-300 text-stone-900"
                    data-testid="notification-gif-url"
                  />
                  <p className="text-xs text-stone-500">
                    Paste a Giphy/direct GIF URL. Shown as a hero image in the email and in-app notification.
                  </p>
                  {gifUrl && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-stone-300 bg-stone-50 relative">
                      <img
                        src={gifUrl}
                        alt="GIF preview"
                        className="w-full max-h-40 object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <button
                        onClick={() => setGifUrl('')}
                        className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-stone-900 transition-colors"
                        title="Remove GIF"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Links Section */}
                <div className="space-y-2">
                  <Label className="text-stone-700 flex items-center gap-2">
                    <Link size={16} />
                    Add Links (Optional)
                  </Label>
                  
                  {/* Existing Links */}
                  {links.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {links.map((link, index) => (
                        <div key={index} className="flex items-center gap-2 bg-stone-100 rounded-lg px-3 py-2 border border-stone-300">
                          <Link size={14} className="text-[#EFB342]" />
                          <span className="text-stone-700 text-sm flex-1 truncate">{link.label}</span>
                          <span className="text-stone-500 text-xs truncate max-w-[150px]">{link.url}</span>
                          <button
                            onClick={() => removeLink(index)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Link Form */}
                  {showLinkForm ? (
                    <div className="p-3 bg-stone-100 rounded-lg border border-stone-300 space-y-2">
                      <Input
                        value={newLinkLabel}
                        onChange={(e) => setNewLinkLabel(e.target.value)}
                        placeholder="Link label (e.g., 'Learn More')"
                        className="bg-white border-stone-300 text-stone-900 text-sm"
                      />
                      <Input
                        value={newLinkUrl}
                        onChange={(e) => setNewLinkUrl(e.target.value)}
                        placeholder="URL (e.g., https://example.com)"
                        className="bg-white border-stone-300 text-stone-900 text-sm"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={addLink}
                          className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                        >
                          <Plus size={14} className="mr-1" /> Add Link
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setShowLinkForm(false); setNewLinkUrl(''); setNewLinkLabel(''); }}
                          className="text-stone-600 hover:text-stone-900"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLinkForm(true)}
                      className="border-stone-300 text-stone-600 hover:text-stone-900 hover:border-[#EFB342]"
                    >
                      <Plus size={14} className="mr-1" /> Add Link
                    </Button>
                  )}
                </div>

                {/* Audience Selection */}
                <div className="space-y-3">
                  <Label className="text-stone-700">Send To</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {audienceOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setAudience(opt.value); if (opt.value !== 'specific') setSelectedUsers([]); }}
                        className={`p-3 rounded-xl border transition-all text-left ${
                          audience === opt.value
                            ? 'bg-[#EFB342]/20 border-[#EFB342] text-stone-900'
                            : 'bg-stone-50 border-stone-300 text-stone-600 hover:border-stone-300'
                        }`}
                        data-testid={`audience-${opt.value}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <opt.icon size={16} className={audience === opt.value ? 'text-[#EFB342]' : ''} />
                          <span className="font-medium text-sm">{opt.label}</span>
                        </div>
                        <p className="text-xs text-stone-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>

                  {/* User Selection (when specific is chosen) */}
                  {audience === 'specific' && (
                    <div className="mt-3 max-h-48 overflow-y-auto bg-stone-50 rounded-xl border border-stone-300 p-2">
                      {users.length === 0 ? (
                        <p className="text-stone-500 text-sm text-center py-4">No users found</p>
                      ) : (
                        <div className="space-y-1">
                          {users.filter(u => !u.is_admin).map((user) => (
                            <button
                              key={user.id}
                              onClick={() => toggleUserSelection(user.id)}
                              className={`w-full p-2 rounded-lg flex items-center justify-between transition-all ${
                                selectedUsers.includes(user.id)
                                  ? 'bg-[#EFB342]/20 text-stone-900'
                                  : 'hover:bg-stone-100 text-stone-600'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-sm">
                                  {user.name?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div className="text-left">
                                  <p className="text-sm font-medium">{user.name}</p>
                                  <p className="text-xs text-stone-500">
                                    {user.email} {user.user_type === 'trial' && <span className="text-purple-400">(Trial)</span>}
                                  </p>
                                </div>
                              </div>
                              {selectedUsers.includes(user.id) && (
                                <CheckCircle2 size={16} className="text-[#EFB342]" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {audience === 'specific' && selectedUsers.length > 0 && (
                    <p className="text-sm text-stone-600">{selectedUsers.length} user(s) selected</p>
                  )}
                </div>

                {/* Only Reachable Users Filter */}
                <div className="flex items-start gap-3 p-4 bg-stone-50/50 rounded-xl border border-stone-200">
                  <button
                    onClick={() => setOnlyReachable(!onlyReachable)}
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${
                      onlyReachable
                        ? 'bg-[#EFB342] border-[#EFB342]'
                        : 'border-stone-300 hover:border-stone-400'
                    }`}
                    data-testid="only-reachable-checkbox"
                  >
                    {onlyReachable && <CheckCircle2 size={14} className="text-black" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <UserCheck size={16} className="text-[#EFB342]" />
                      <span className="font-medium text-stone-900">Only reachable users</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Excludes users with no email address and no active push subscription — they cannot receive anything from this broadcast anyway.
                    </p>
                  </div>
                </div>

                {/* Email Option */}
                <div className="flex items-center gap-3 p-4 bg-stone-50/50 rounded-xl border border-stone-200">
                  <button
                    onClick={() => setSendEmail(!sendEmail)}
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                      sendEmail 
                        ? 'bg-[#EFB342] border-[#EFB342]' 
                        : 'border-stone-300 hover:border-stone-400'
                    }`}
                    data-testid="send-email-checkbox"
                  >
                    {sendEmail && <CheckCircle2 size={14} className="text-black" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-[#EFB342]" />
                      <span className="font-medium text-stone-900">Also send email</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Send this notification to users' email addresses
                    </p>
                  </div>
                </div>

                {/* Send/Schedule/Update Buttons */}
                {editingScheduled ? (
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCancelEdit}
                      variant="outline"
                      className="flex-1 h-12 border-stone-300 text-stone-700 hover:bg-stone-100"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveScheduledEdit}
                      disabled={savingScheduled || !title.trim() || !message.trim() || (audience === 'specific' && selectedUsers.length === 0)}
                      className="flex-1 h-12 bg-purple-600 hover:bg-purple-700 text-stone-900 font-semibold"
                      data-testid="update-scheduled-btn"
                    >
                      {savingScheduled ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          Updating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CheckCircle2 size={18} />
                          Update Scheduled
                        </span>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleSend}
                    disabled={sending || previewingAudience || !title.trim() || !message.trim() || (audience === 'specific' && selectedUsers.length === 0)}
                    className={`w-full font-semibold h-12 ${
                      scheduleMode === 'later' 
                        ? 'bg-purple-600 hover:bg-purple-700 text-stone-900' 
                        : 'bg-[#EFB342] text-black hover:bg-[#D99F30]'
                    }`}
                    data-testid="send-notification-btn"
                  >
                    {sending || previewingAudience ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        {previewingAudience ? 'Checking audience…' : (scheduleMode === 'later' ? 'Scheduling...' : 'Sending...')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {scheduleMode === 'later' ? <Calendar size={18} /> : <Send size={18} />}
                        {scheduleMode === 'later' ? 'Review & Schedule' : 'Review & Send'}
                      </span>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Pending Scheduled Notifications */}
            {pendingScheduled.length > 0 && (
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-bold text-stone-900 flex items-center gap-2">
                    <Clock size={18} className="text-purple-400" />
                    Scheduled ({pendingScheduled.length})
                  </h2>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleProcessScheduled}
                    disabled={processingScheduled}
                    className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
                    data-testid="process-scheduled-btn"
                  >
                    {processingScheduled ? (
                      <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Play size={14} className="mr-1" />
                        Process Due
                      </>
                    )}
                  </Button>
                </div>
                <div className="space-y-3">
                  {pendingScheduled.map((scheduled) => (
                    <div key={scheduled.id} className="bg-white rounded-xl p-4 border border-stone-200">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-stone-900 text-sm truncate">{scheduled.title}</h4>
                          <p className="text-stone-500 text-xs mt-1 line-clamp-2">{scheduled.message}</p>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span className="text-xs text-purple-400 flex items-center gap-1">
                              <Calendar size={12} />
                              {formatScheduledDate(scheduled.send_at)}
                            </span>
                            <span className="text-xs text-stone-500 capitalize">
                              To: {scheduled.audience === 'all' ? 'All Users' : 
                                   scheduled.audience === 'paid' ? 'Paid Only' :
                                   scheduled.audience === 'trial' ? 'Trial Only' : 
                                   `${scheduled.user_ids?.length || 0} users`}
                            </span>
                            {scheduled.send_email && (
                              <span className="text-xs text-[#EFB342] flex items-center gap-1">
                                <Mail size={12} />
                                + Email
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditScheduled(scheduled)}
                            className="text-stone-600 hover:text-stone-900 hover:bg-stone-200"
                            data-testid={`edit-scheduled-${scheduled.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCancelScheduled(scheduled.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            data-testid={`cancel-scheduled-${scheduled.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* History */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6">
            <h2 className="font-heading text-xl font-bold text-stone-900 mb-6 flex items-center gap-2">
              <History size={20} className="text-[#EFB342]" />
              Recent History
            </h2>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12">
                <Bell size={40} className="mx-auto text-stone-400 mb-3" />
                <p className="text-stone-600">No notifications sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {history.map((item, index) => {
                  const TypeIcon = notificationTypes.find(t => t.value === item.notification_type)?.icon || MessageSquare;
                  const isExpanded = expandedHistoryIndex === index;
                  const isLongMessage = item.message && item.message.length > 80;
                  
                  const formatMessage = (msg) => {
                    if (!msg) return '';
                    return msg.split('\n').map((line, i) => (
                      <span key={i}>
                        {line}
                        {i < msg.split('\n').length - 1 && <br />}
                      </span>
                    ));
                  };
                  
                  return (
                    <div
                      key={index}
                      className={`bg-stone-50/50 rounded-xl border transition-all ${
                        isExpanded ? 'border-[#EFB342]/50 ring-1 ring-[#EFB342]/30' : 'border-stone-200'
                      }`}
                    >
                      <button
                        onClick={() => setExpandedHistoryIndex(isExpanded ? null : index)}
                        className="w-full p-4 text-left hover:bg-[#F0EBE1] transition-colors rounded-xl"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                            <TypeIcon size={18} className="text-stone-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-medium text-stone-900 text-sm">{item.title}</h4>
                              {isLongMessage && (
                                isExpanded ? (
                                  <ChevronUp size={16} className="text-stone-500 flex-shrink-0" />
                                ) : (
                                  <ChevronDown size={16} className="text-stone-500 flex-shrink-0" />
                                )
                              )}
                            </div>
                            <p className={`text-stone-500 text-xs mt-0.5 ${!isExpanded && isLongMessage ? 'line-clamp-2' : ''}`}>
                              {isExpanded ? formatMessage(item.message) : item.message}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs text-stone-400 flex items-center gap-1">
                                <Users size={12} />
                                {item.recipient_count} recipient{item.recipient_count !== 1 ? 's' : ''}
                              </span>
                              <span className="text-xs text-stone-400 flex items-center gap-1">
                                <CheckCircle2 size={12} />
                                {item.read_count} read
                              </span>
                              <span className="text-xs text-stone-400">
                                {formatDate(item.sent_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                      
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-stone-200 mt-0">
                          <div className="flex justify-between items-center pt-3 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendBatch({
                                title: item.title,
                                message: item.message,
                                notification_type: item.notification_type,
                                audience: 'all',
                                send_email: false,
                              })}
                              className="border-[#EFB342] text-[#B8871E] hover:bg-[#FFF8E7] hover:border-[#B8871E] text-xs"
                              data-testid={`resend-history-${index}`}
                            >
                              <Send size={14} className="mr-1" />
                              Resend
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedHistoryIndex(null)}
                              className="text-stone-600 hover:text-stone-900 text-xs"
                            >
                              <X size={14} className="mr-1" />
                              Close
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Delivery Tracking */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6">
            <h2 className="font-heading text-xl font-bold text-stone-900 mb-6 flex items-center gap-2">
              <BarChart3 size={20} className="text-[#EFB342]" />
              Delivery Tracking
            </h2>

            {loadingBatches ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : batches.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 size={40} className="mx-auto text-stone-400 mb-3" />
                <p className="text-stone-600">No broadcast history available</p>
                <p className="text-stone-500 text-sm mt-1">Send a notification to see delivery stats</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {batches.map((batch) => {
                  const stats = batch.stats || {};
                  const successRate = stats.notifications_sent > 0 
                    ? Math.round((stats.notifications_sent / stats.total_targeted) * 100) 
                    : 0;
                  const hasFailures = stats.emails_failed > 0 || stats.skipped_expired_trials > 0;
                  
                  return (
                    <div 
                      key={batch.id} 
                      className="bg-stone-50/50 rounded-xl border border-stone-200 p-4 hover:border-stone-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-stone-900 text-sm truncate">{batch.title}</h4>
                          <p className="text-stone-500 text-xs mt-1 line-clamp-1">{batch.message}</p>
                          <div className="flex items-center gap-4 mt-3 flex-wrap">
                            <span className="text-xs flex items-center gap-1.5">
                              <CheckCheck size={14} className="text-green-400" />
                              <span className="text-green-400">{stats.notifications_sent || 0}</span>
                              <span className="text-stone-500">sent</span>
                            </span>
                            {batch.send_email && (
                              <span className="text-xs flex items-center gap-1.5">
                                <Mail size={14} className="text-[#EFB342]" />
                                <span className="text-[#EFB342]">{stats.emails_sent || 0}</span>
                                <span className="text-stone-500">emails</span>
                              </span>
                            )}
                            {stats.emails_failed > 0 && (
                              <span className="text-xs flex items-center gap-1.5">
                                <XCircle size={14} className="text-red-400" />
                                <span className="text-red-400">{stats.emails_failed}</span>
                                <span className="text-stone-500">failed</span>
                              </span>
                            )}
                            {stats.skipped_expired_trials > 0 && (
                              <span className="text-xs flex items-center gap-1.5">
                                <AlertTriangle size={14} className="text-amber-400" />
                                <span className="text-amber-400">{stats.skipped_expired_trials}</span>
                                <span className="text-stone-500">expired trials</span>
                              </span>
                            )}
                            {batch.email_tracking && (
                              <>
                                <span className="text-xs flex items-center gap-1.5">
                                  <Eye size={14} className="text-blue-400" />
                                  <span className="text-blue-400">{batch.email_tracking.opens || 0}</span>
                                  <span className="text-stone-500">opens ({batch.email_tracking.open_rate || 0}%)</span>
                                </span>
                                <span className="text-xs flex items-center gap-1.5">
                                  <span className="text-purple-400">🔗</span>
                                  <span className="text-purple-400">{batch.email_tracking.clicks || 0}</span>
                                  <span className="text-stone-500">clicks ({batch.email_tracking.click_rate || 0}%)</span>
                                </span>
                              </>
                            )}
                            <span className="text-xs text-stone-400">
                              {formatDate(batch.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resendBatch(batch)}
                            className="border-[#EFB342] text-[#B8871E] hover:bg-[#FFF8E7] hover:text-[#B8871E] hover:border-[#B8871E]"
                            data-testid={`resend-batch-${batch.id}`}
                          >
                            <Send size={14} className="mr-1" />
                            Resend
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fetchBatchDetails(batch.id)}
                            disabled={loadingBatchDetails && selectedBatch === batch.id}
                            className="border-stone-300 text-stone-600 hover:text-stone-900 hover:border-[#EFB342]"
                            data-testid={`view-delivery-${batch.id}`}
                          >
                            {loadingBatchDetails && selectedBatch === batch.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <>
                                <Eye size={14} className="mr-1" />
                                Details
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="mt-3 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${hasFailures ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${successRate}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-stone-400">{stats.total_targeted || 0} targeted</span>
                        <span className={`text-xs ${hasFailures ? 'text-amber-400' : 'text-green-400'}`}>
                          {successRate}% delivered
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delivery Details Modal */}
      <Dialog open={deliveryModalOpen} onOpenChange={setDeliveryModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-white border-stone-300">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <BarChart3 size={20} className="text-[#EFB342]" />
              Delivery Report
            </DialogTitle>
            <DialogDescription className="text-stone-600 text-sm">
              View notification delivery status and engagement metrics
            </DialogDescription>
          </DialogHeader>
          
          {batchDetails && (
            <div className="space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Batch Info */}
              <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                <h3 className="font-medium text-stone-900">{batchDetails.title}</h3>
                <p className="text-stone-600 text-sm mt-1 line-clamp-2">{batchDetails.message}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                  <span>Audience: <span className="text-stone-700 capitalize">{batchDetails.audience}</span></span>
                  <span>Sent: {formatDate(batchDetails.created_at)}</span>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-stone-50 rounded-xl p-3 border border-stone-200 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {batchDetails.stats?.notifications_sent || 0}
                  </div>
                  <div className="text-xs text-stone-500 mt-1">Delivered</div>
                </div>
                <div className="bg-stone-50 rounded-xl p-3 border border-stone-200 text-center">
                  <div className="text-2xl font-bold text-[#EFB342]">
                    {batchDetails.stats?.emails_sent || 0}
                  </div>
                  <div className="text-xs text-stone-500 mt-1">Emails Sent</div>
                </div>
                <div className="bg-stone-50 rounded-xl p-3 border border-stone-200 text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {batchDetails.stats?.emails_failed || 0}
                  </div>
                  <div className="text-xs text-stone-500 mt-1">Failed</div>
                </div>
                <div className="bg-stone-50 rounded-xl p-3 border border-stone-200 text-center">
                  <div className="text-2xl font-bold text-amber-400">
                    {(batchDetails.stats?.skipped_duplicates || 0) + (batchDetails.stats?.skipped_expired_trials || 0)}
                  </div>
                  <div className="text-xs text-stone-500 mt-1">Skipped</div>
                </div>
              </div>

              {/* Email Tracking Stats */}
              {batchDetails.email_tracking_stats && batchDetails.send_email && (
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                  <h4 className="text-sm font-medium text-stone-900 mb-3 flex items-center gap-2">
                    <Mail size={16} className="text-[#EFB342]" />
                    Email Engagement
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-400">
                        {batchDetails.email_tracking_stats.opens || 0}
                      </div>
                      <div className="text-xs text-stone-500">Opens</div>
                      <div className="text-xs text-blue-400">{batchDetails.email_tracking_stats.open_rate || 0}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-400">
                        {batchDetails.email_tracking_stats.clicks || 0}
                      </div>
                      <div className="text-xs text-stone-500">Clicks</div>
                      <div className="text-xs text-purple-400">{batchDetails.email_tracking_stats.click_rate || 0}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-400">
                        {batchDetails.email_tracking_stats.unique_clickers || 0}
                      </div>
                      <div className="text-xs text-stone-500">Unique Clickers</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-stone-600">
                        {batchDetails.stats?.emails_sent - batchDetails.email_tracking_stats.opens || 0}
                      </div>
                      <div className="text-xs text-stone-500">Unopened</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Delivery Records Table */}
              <div className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden">
                <div className="p-3 border-b border-stone-200">
                  <h4 className="text-sm font-medium text-stone-900 flex items-center gap-2">
                    <Users size={16} />
                    Recipient Details ({batchDetails.delivery_records?.length || 0})
                  </h4>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white sticky top-0">
                      <tr className="text-left text-stone-500 text-xs">
                        <th className="p-3">User</th>
                        <th className="p-3">Notification</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Opened</th>
                        <th className="p-3">Clicked</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {batchDetails.delivery_records?.map((record, idx) => (
                        <tr key={idx} className="hover:bg-white">
                          <td className="p-3">
                            <div>
                              <p className="text-stone-900 text-sm">{record.user_name}</p>
                              <p className="text-stone-500 text-xs">{record.user_email}</p>
                              {record.user_type === 'trial' && (
                                <span className="text-xs text-purple-400">(Trial)</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            {record.notification_status === 'sent' ? (
                              <span className="flex items-center gap-1 text-green-400 text-xs">
                                <CheckCircle2 size={14} />
                                Sent
                              </span>
                            ) : record.notification_status === 'skipped' ? (
                              <span className="flex items-center gap-1 text-amber-400 text-xs">
                                <AlertTriangle size={14} />
                                {record.skip_reason === 'expired_trial' ? 'Expired Trial' : 
                                 record.skip_reason === 'recently_sent' ? 'Recently Sent' :
                                 record.skip_reason === 'duplicate_email' ? 'Duplicate' : 'Skipped'}
                              </span>
                            ) : (
                              <span className="text-stone-500 text-xs">Pending</span>
                            )}
                          </td>
                          <td className="p-3">
                            {record.email_status === 'sent' ? (
                              <span className="flex items-center gap-1 text-green-400 text-xs">
                                <Mail size={14} />
                                Sent
                              </span>
                            ) : record.email_status === 'failed' ? (
                              <span className="flex items-center gap-1 text-red-400 text-xs" title={record.email_error}>
                                <XCircle size={14} />
                                Failed
                              </span>
                            ) : record.email_status === 'skipped' ? (
                              <span className="text-stone-500 text-xs">
                                {record.email_error === 'no_email_address' ? 'No Email' : 'Skipped'}
                              </span>
                            ) : record.email_status === 'not_requested' ? (
                              <span className="text-stone-400 text-xs">—</span>
                            ) : (
                              <span className="text-stone-500 text-xs">Pending</span>
                            )}
                          </td>
                          <td className="p-3">
                            {record.email_status === 'sent' ? (
                              record.email_opened ? (
                                <span className="flex items-center gap-1 text-blue-400 text-xs">
                                  <Eye size={14} />
                                  Yes
                                </span>
                              ) : (
                                <span className="text-stone-500 text-xs">No</span>
                              )
                            ) : (
                              <span className="text-stone-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="p-3">
                            {record.email_status === 'sent' ? (
                              record.email_clicked ? (
                                <span className="flex flex-col gap-0.5">
                                  <span className="flex items-center gap-1 text-purple-400 text-xs">
                                    🔗 {record.clicks?.length || 0} click(s)
                                  </span>
                                  {record.clicks?.slice(0, 2).map((click, i) => (
                                    <span key={i} className="text-stone-500 text-xs truncate max-w-[100px]" title={click.url}>
                                      {click.url?.replace(/https?:\/\//, '').slice(0, 20)}...
                                    </span>
                                  ))}
                                </span>
                              ) : (
                                <span className="text-stone-500 text-xs">No</span>
                              )
                            ) : (
                              <span className="text-stone-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pre-send Confirmation Dialog — safety net after prod misfire (Jul 13, 2026) */}
      <Dialog open={!!confirmDialog} onOpenChange={(o) => { if (!o) setConfirmDialog(null); }}>
        <DialogContent className="max-w-lg bg-white border-stone-300" data-testid="send-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-400" />
              {confirmDialog?.isScheduled ? 'Confirm scheduled send' : 'Confirm send'}
            </DialogTitle>
            <DialogDescription className="text-stone-600 text-sm">
              Review the audience before {confirmDialog?.isScheduled ? 'scheduling' : 'sending'}. This is your last chance to cancel.
            </DialogDescription>
          </DialogHeader>

          {confirmDialog && (
            <div className="space-y-4">
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-stone-600 text-sm">Audience</span>
                  <span className="text-stone-900 font-medium capitalize">
                    {confirmDialog.audience === 'never_tracked' ? 'Never Tracked' : confirmDialog.audience === 'all' ? 'All Users' : confirmDialog.audience === 'paid' ? 'Paid Only' : confirmDialog.audience === 'trial' ? 'Trial Only' : 'Specific'}
                    {confirmDialog.onlyReachable && (
                      <span className="ml-2 text-xs text-[#EFB342]">· reachable only</span>
                    )}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-stone-600 text-sm">Recipients</span>
                  <span className={`text-2xl font-bold ${confirmDialog.count === 0 ? 'text-red-400' : 'text-[#EFB342]'}`} data-testid="confirm-recipient-count">
                    {confirmDialog.count}
                  </span>
                </div>
                {confirmDialog.reachability && confirmDialog.count > 0 && (
                  <div className="pt-2 mt-2 border-t border-stone-200 grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <div className="text-stone-900 font-medium">{confirmDialog.reachability.with_email}</div>
                      <div className="text-stone-500">have email</div>
                    </div>
                    <div>
                      <div className="text-stone-900 font-medium">{confirmDialog.reachability.with_push}</div>
                      <div className="text-stone-500">have push</div>
                    </div>
                    <div>
                      <div className={`font-medium ${confirmDialog.reachability.without_any > 0 ? 'text-amber-400' : 'text-stone-900'}`}>{confirmDialog.reachability.without_any}</div>
                      <div className="text-stone-500">unreachable</div>
                    </div>
                  </div>
                )}
                {confirmDialog.isScheduled && (
                  <div className="flex items-baseline justify-between pt-2 mt-2 border-t border-stone-200">
                    <span className="text-stone-600 text-sm">Scheduled for</span>
                    <span className="text-purple-300 text-sm">{confirmDialog.scheduledFor}</span>
                  </div>
                )}
              </div>

              {confirmDialog.count === 0 ? (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
                  No users match this audience. Nothing will be sent.
                </div>
              ) : (
                <div>
                  <p className="text-stone-600 text-xs mb-2">First few recipients:</p>
                  <div className="bg-stone-50 border border-stone-200 rounded-lg divide-y divide-zinc-800 max-h-40 overflow-y-auto">
                    {confirmDialog.sample.map((u) => (
                      <div key={u.id} className="px-3 py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-stone-900 text-sm truncate">{u.name}</p>
                          <p className="text-stone-500 text-xs truncate">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span title={u.has_email ? 'Has email' : 'No email'} className={u.has_email ? 'text-[#EFB342]' : 'text-stone-400'}>
                            <Mail size={13} />
                          </span>
                          <span title={u.has_push ? 'Has push subscription' : 'No push subscription'} className={u.has_push ? 'text-[#EFB342]' : 'text-stone-400'}>
                            <Bell size={13} />
                          </span>
                          {u.user_type === 'trial' && (
                            <span className="text-xs text-purple-400 ml-1">Trial</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {confirmDialog.count > confirmDialog.sample.length && (
                      <div className="px-3 py-2 text-stone-500 text-xs">
                        + {confirmDialog.count - confirmDialog.sample.length} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialog(null)}
                  className="flex-1 border-stone-300 text-stone-700 hover:bg-stone-100"
                  data-testid="confirm-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeSend}
                  disabled={confirmDialog.count === 0 || sending}
                  className={`flex-1 font-semibold ${confirmDialog.isScheduled ? 'bg-purple-600 hover:bg-purple-700 text-stone-900' : 'bg-[#EFB342] text-black hover:bg-[#D99F30]'}`}
                  data-testid="confirm-send-btn"
                >
                  {confirmDialog.isScheduled ? `Schedule for ${confirmDialog.count}` : `Send to ${confirmDialog.count}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminNotifications;
